import { F0Select, F0SelectItemObject } from '@factorialco/f0-react'
import {
  BulkPaymentSelectorPaymentsDocument,
  BulkPaymentSelectorPaymentsQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

type BulkPaymentOption = NonNullable<
  BulkPaymentSelectorPaymentsQuery['banking']
>['paymentsConnection']['edges'][number]['node']

export interface BulkPaymentSelectorProps {
  value?: number
  onChange: (value: number, payment?: BulkPaymentOption) => void
  label?: string
  placeholder?: string
  error?: string
}

export const BulkPaymentSelector = ({
  value,
  onChange,
  label = i18n.t('banking.bulk_payment.forms.fields.payment.label'),
  placeholder = i18n.t('banking.bulk_payment.forms.fields.payment.placeholder'),
  error,
}: BulkPaymentSelectorProps) => {
  const source = useGraphqlDataSource(
    {
      document: BulkPaymentSelectorPaymentsDocument,
      paginationType: 'infinite-scroll',
      perPage: 20,
      mapVariables: ({ search }) => ({
        search: search || undefined,
      }),
      mapData: (data) => data.banking?.paymentsConnection?.edges?.map((edge) => edge.node) ?? [],
      mapPagination: (data) => ({
        total: data.banking?.paymentsConnection?.totalCount ?? 0,
        cursor: data.banking?.paymentsConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: data.banking?.paymentsConnection?.pageInfo?.hasNextPage ?? false,
      }),
    },
    []
  )

  const mapOptions = (
    payment: BulkPaymentOption
  ): F0SelectItemObject<string, BulkPaymentOption> => ({
    value: String(payment.id),
    label: payment.label,
    description: i18n.t('banking.bulk_payment.selector.payment_requests_count', {
      count: payment.paymentRequestsCount,
    }),
    item: payment,
  })

  return (
    <F0Select<string, BulkPaymentOption>
      label={label}
      placeholder={placeholder}
      source={source}
      mapOptions={mapOptions}
      value={value !== undefined && value !== -1 ? String(value) : undefined}
      onChange={(selectedValue: string, originalItem?: BulkPaymentOption) => {
        const paymentId = Number(selectedValue)
        onChange(paymentId, originalItem)
      }}
      error={error}
    />
  )
}
